import { ISessionExecutor, ISessionExecutionResult } from '../../interfaces/core/ISessionExecutor';
import { ITestSession } from '../../interfaces/core/ITestSession';
import { ITestEnvironment } from '../../interfaces/test-environment/ITestEnvironment';
import { IEventBus } from '../../interfaces/events/IEventBus';

export class SessionExecutor implements ISessionExecutor {
  constructor(
    private testEnvironment: ITestEnvironment,
    private eventBus: IEventBus
  ) {}

  async execute(session: ITestSession): Promise<ISessionExecutionResult> {
    if (!this.canExecute(session)) {
      throw new Error(`Cannot execute session '${session.label}': ${session.error?.message || 'Unknown error'}`);
    }

    const startTime = Date.now();
    
    this.eventBus.emit({
      type: 'session:execution-started',
      sessionLabel: session.label,
      timestamp: new Date().toISOString(),
      data: { sessionId: session.id, sessionLabel: session.label }
    });

    try {
      const mochaInstance = this.testEnvironment.createMochaInstance(session.label);
      this.testEnvironment.injectGlobals(mochaInstance);

      // Re-run setup if needed
      const setupFn = (session as any).setupFn;
      if (!session.completed && setupFn) {
        const result = setupFn();
        if (result instanceof Promise) {
          await result;
        }
      }

      const testResult = await this.runTests(mochaInstance);
      const duration = Date.now() - startTime;

      session.markCompleted();

      const result: ISessionExecutionResult = {
        sessionId: session.id,
        sessionLabel: session.label,
        stats: testResult.stats,
        success: testResult.success,
        error: testResult.error,
        duration
      };

      this.eventBus.emit({
        type: 'session:execution-completed',
        sessionLabel: session.label,
        timestamp: new Date().toISOString(),
        data: result
      });

      return result;

    } catch (error) {
      const duration = Date.now() - startTime;
      session.setError(error as Error);

      const result: ISessionExecutionResult = {
        sessionId: session.id,
        sessionLabel: session.label,
        stats: { tests: 0, passes: 0, pending: 0, failures: 1 },
        success: false,
        error: error as Error,
        duration
      };

      this.eventBus.emit({
        type: 'session:failed',
        sessionLabel: session.label,
        timestamp: new Date().toISOString(),
        data: { sessionId: session.id, error: (error as Error).message }
      });

      throw error;
    }
  }

  canExecute(session: ITestSession): boolean {
    return !session.error;
  }

  private async runTests(mochaInstance: any): Promise<{ stats: any; success: boolean; error?: Error }> {
    return new Promise((resolve) => {
      const runner = mochaInstance.run();
      
      let failures = 0;
      let passes = 0;
      let tests = 0;
      
      runner.on('pass', () => {
        passes++;
        tests++;
      });
      
      runner.on('fail', () => {
        failures++;
        tests++;
      });
      
      runner.on('end', () => {
        const stats = mochaInstance.stats || { passes, failures, tests, pending: 0 };
        resolve({
          stats,
          success: failures === 0
        });
      });
    });
  }
}
